import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiFetch } from './utils/api';
import PageHeader from './components/PageHeader';

function ReportBuilder() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = id === 'new';

  const [report, setReport] = useState({
    name: '',
    description: '',
    data_source: '',
    query_config: {
      fields: [],
      joins: [],
      filters: [],
      groupBy: [],
      aggregates: [],
      orderBy: [],
      limit: 100
    }
  });

  const [dataSources, setDataSources] = useState([]);
  const [selectedSource, setSelectedSource] = useState(null);
  const [availableFields, setAvailableFields] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    fetchDataSources();
    if (!isNew) fetchReport();
  }, [id]);

  useEffect(() => {
    if (report.data_source) {
      fetchDataSourceDetails(report.data_source);
    }
  }, [report.data_source]);

  const fetchDataSources = async () => {
    try {
      const res = await apiFetch('/api/report-data-sources');
      
      if (!res.ok) {
        throw new Error('Failed to fetch data sources');
      }
      
      const data = await res.json();
      
      if (data.error) {
        throw new Error(data.error);
      }
      
      setDataSources(data);
    } catch (err) {
      console.error('Error fetching data sources:', err);
      alert('Failed to load data sources: ' + err.message);
    }
  };

  const fetchDataSourceDetails = async (sourceName) => {
    try {
      const res = await apiFetch(`/api/report-data-sources/${sourceName}`);
      const data = await res.json();
      setSelectedSource(data);
      
      // Build available fields list
      const fields = [
        ...data.fields.map(f => ({ ...f, source: 'main' })),
        ...(data.joins || []).flatMap(join =>
          join.fields.map(f => ({ ...f, source: join.alias, joinLabel: join.label }))
        )
      ];
      setAvailableFields(fields);
    } catch (err) {
      console.error('Error fetching data source details:', err);
    }
  };

  const fetchReport = async () => {
    try {
      setLoading(true);
      const res = await apiFetch(`/api/reports/${id}`);
      const data = await res.json();
      setReport(data);
    } catch (err) {
      console.error('Error fetching report:', err);
      alert('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!report.name || !report.data_source) {
      alert('Please provide a name and select a data source');
      return;
    }

    if (report.query_config.fields.length === 0) {
      alert('Please select at least one field');
      return;
    }

    try {
      setSaving(true);
      const method = isNew ? 'POST' : 'PUT';
      const url = isNew 
        ? '/api/reports'
        : `/api/reports/${id}`;
      
      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(report)
      });

      const saved = await res.json();
      navigate(`/reports/${saved.id}/run`);
    } catch (err) {
      console.error('Error saving report:', err);
      alert('Failed to save report');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    if (!report.data_source || report.query_config.fields.length === 0) {
      alert('Please select a data source and at least one field');
      return;
    }

    try {
      setPreviewLoading(true);
      const res = await apiFetch('/api/reports/execute', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dataSource: report.data_source,
          ...report.query_config,
          limit: 10 // Preview only
        })
      });

      const result = await res.json();
      setPreviewData(result);
    } catch (err) {
      console.error('Error previewing report:', err);
      alert('Failed to preview report');
    } finally {
      setPreviewLoading(false);
    }
  };

  const toggleField = (fieldName, fieldSource) => {
    const fullField = fieldSource === 'main' ? fieldName : `${fieldSource}.${fieldName}`;
    const fields = report.query_config.fields;
    
    if (fields.includes(fullField)) {
      // Remove field
      setReport({
        ...report,
        query_config: {
          ...report.query_config,
          fields: fields.filter(f => f !== fullField)
        }
      });
    } else {
      // Add field
      setReport({
        ...report,
        query_config: {
          ...report.query_config,
          fields: [...fields, fullField]
        }
      });

      // Auto-add join if needed
      if (fieldSource !== 'main') {
        const join = selectedSource.joins.find(j => j.alias === fieldSource);
        if (join && !report.query_config.joins.find(j => j.alias === fieldSource)) {
          setReport({
            ...report,
            query_config: {
              ...report.query_config,
              joins: [...report.query_config.joins, {
                table: join.table,
                alias: join.alias,
                on: join.on
              }]
            }
          });
        }
      }
    }
  };

  const addSortField = () => {
    setReport({
      ...report,
      query_config: {
        ...report.query_config,
        orderBy: [...report.query_config.orderBy, { field: '', direction: 'ASC' }]
      }
    });
  };

  const updateSortField = (index, field, direction) => {
    const orderBy = [...report.query_config.orderBy];
    orderBy[index] = { field, direction };
    setReport({
      ...report,
      query_config: { ...report.query_config, orderBy }
    });
  };

  const removeSortField = (index) => {
    setReport({
      ...report,
      query_config: {
        ...report.query_config,
        orderBy: report.query_config.orderBy.filter((_, i) => i !== index)
      }
    });
  };

  // Filter management
  const addFilter = () => {
    setReport({
      ...report,
      query_config: {
        ...report.query_config,
        filters: [...report.query_config.filters, { field: '', operator: 'equals', value: '' }]
      }
    });
  };

  const updateFilter = (index, field, operator, value) => {
    const filters = [...report.query_config.filters];
    filters[index] = { field, operator, value };
    setReport({
      ...report,
      query_config: { ...report.query_config, filters }
    });
  };

  const removeFilter = (index) => {
    setReport({
      ...report,
      query_config: {
        ...report.query_config,
        filters: report.query_config.filters.filter((_, i) => i !== index)
      }
    });
  };

  // Get field type for smart operator selection
  const getFieldType = (fieldName) => {
    const field = availableFields.find(f => {
      if (fieldName.includes('.')) {
        const [alias, name] = fieldName.split('.');
        return f.source === alias && f.name === name;
      }
      return f.source === 'main' && f.name === fieldName;
    });
    return field ? field.type : 'text';
  };

  // Get appropriate operators for field type
  const getOperatorsForType = (type) => {
    switch (type) {
      case 'date':
      case 'timestamp':
        return [
          { value: 'equals', label: 'Equals' },
          { value: 'not_equals', label: 'Not equals' },
          { value: 'greater_than', label: 'After' },
          { value: 'less_than', label: 'Before' },
          { value: 'between', label: 'Between' },
          { value: 'last_n_days', label: 'Last N days' },
          { value: 'this_week', label: 'This week' },
          { value: 'this_month', label: 'This month' },
          { value: 'this_quarter', label: 'This quarter' },
          { value: 'this_year', label: 'This year' },
          { value: 'is_null', label: 'Is empty' },
          { value: 'is_not_null', label: 'Is not empty' }
        ];
      case 'number':
      case 'integer':
      case 'decimal':
        return [
          { value: 'equals', label: '=' },
          { value: 'not_equals', label: '≠' },
          { value: 'greater_than', label: '>' },
          { value: 'less_than', label: '<' },
          { value: 'greater_or_equal', label: '≥' },
          { value: 'less_or_equal', label: '≤' },
          { value: 'between', label: 'Between' },
          { value: 'is_null', label: 'Is empty' },
          { value: 'is_not_null', label: 'Is not empty' }
        ];
      default: // text/varchar
        return [
          { value: 'equals', label: 'Equals' },
          { value: 'not_equals', label: 'Not equals' },
          { value: 'contains', label: 'Contains' },
          { value: 'not_contains', label: 'Does not contain' },
          { value: 'starts_with', label: 'Starts with' },
          { value: 'ends_with', label: 'Ends with' },
          { value: 'is_empty', label: 'Is empty' },
          { value: 'is_not_empty', label: 'Is not empty' }
        ];
    }
  };

  // Check if operator needs value input
  const operatorNeedsValue = (operator) => {
    return !['is_null', 'is_not_null', 'is_empty', 'is_not_empty', 'this_week', 'this_month', 'this_quarter', 'this_year'].includes(operator);
  };

  // Grouping management
  const addGroupByField = () => {
    setReport({
      ...report,
      query_config: {
        ...report.query_config,
        groupBy: [...report.query_config.groupBy, '']
      }
    });
  };

  const updateGroupByField = (index, field) => {
    const groupBy = [...report.query_config.groupBy];
    groupBy[index] = field;
    setReport({
      ...report,
      query_config: { ...report.query_config, groupBy }
    });
  };

  const removeGroupByField = (index) => {
    setReport({
      ...report,
      query_config: {
        ...report.query_config,
        groupBy: report.query_config.groupBy.filter((_, i) => i !== index)
      }
    });
  };

  // Aggregate management
  const addAggregate = () => {
    setReport({
      ...report,
      query_config: {
        ...report.query_config,
        aggregates: [...report.query_config.aggregates, { func: 'COUNT', field: '*', alias: '' }]
      }
    });
  };

  const updateAggregate = (index, func, field, alias) => {
    const aggregates = [...report.query_config.aggregates];
    aggregates[index] = { func, field, alias };
    setReport({
      ...report,
      query_config: { ...report.query_config, aggregates }
    });
  };

  const removeAggregate = (index) => {
    setReport({
      ...report,
      query_config: {
        ...report.query_config,
        aggregates: report.query_config.aggregates.filter((_, i) => i !== index)
      }
    });
  };

  // Get numeric fields for aggregates
  const getNumericFields = () => {
    return availableFields.filter(f => ['number', 'integer', 'decimal'].includes(f.type));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading report...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader 
        title={isNew ? 'New Report' : 'Edit Report'}
        subtitle="Build your custom report"
        backButton
        onBack={() => navigate('/reports')}
        backTitle="Back to Reports"
        sticky
        actionButton={
          <div className="flex space-x-2">
            <button
              onClick={handlePreview}
              disabled={previewLoading}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-gray-700 disabled:opacity-50"
            >
              {previewLoading ? 'Loading...' : '👁️ Preview'}
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : '💾 Save & Run'}
            </button>
          </div>
        }
      />

      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="grid grid-cols-3 gap-6">
          {/* Left Panel - Report Info & Data Source */}
          <div className="space-y-6">
            {/* Report Info */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Report Details</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Report Name *
                  </label>
                  <input
                    type="text"
                    value={report.name}
                    onChange={(e) => setReport({ ...report, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Monthly Revenue Report"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={report.description || ''}
                    onChange={(e) => setReport({ ...report, description: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    rows="3"
                    placeholder="What does this report show?"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data Source *
                  </label>
                  <select
                    value={report.data_source}
                    onChange={(e) => setReport({ ...report, data_source: e.target.value, query_config: { ...report.query_config, fields: [], joins: [] } })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    disabled={!isNew}
                  >
                    <option value="">Select source...</option>
                    {dataSources.map(source => (
                      <option key={source.id} value={source.id}>
                        {source.label}
                      </option>
                    ))}
                  </select>
                  {!isNew && (
                    <p className="text-xs text-gray-500 mt-1">Cannot change data source after creation</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Row Limit
                  </label>
                  <input
                    type="number"
                    value={report.query_config.limit}
                    onChange={(e) => setReport({ ...report, query_config: { ...report.query_config, limit: parseInt(e.target.value) || 100 } })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    min="1"
                    max="10000"
                  />
                  <p className="text-xs text-gray-500 mt-1">Max 10,000 rows</p>
                </div>
              </div>
            </div>

            {/* Available Fields */}
            {selectedSource && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="font-semibold text-gray-900 mb-4">
                  Available Fields ({report.query_config.fields.length} selected)
                </h3>
                
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {/* Main table fields */}
                  <div>
                    <div className="text-sm font-medium text-gray-700 mb-2">
                      {selectedSource.label}
                    </div>
                    <div className="space-y-1">
                      {selectedSource.fields.map(field => (
                        <label key={field.name} className="flex items-center space-x-2 text-sm hover:bg-gray-50 p-1 rounded cursor-pointer">
                          <input
                            type="checkbox"
                            checked={report.query_config.fields.includes(field.name)}
                            onChange={() => toggleField(field.name, 'main')}
                            className="rounded"
                          />
                          <span>{field.label}</span>
                          <span className="text-xs text-gray-400">({field.type})</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Joined table fields */}
                  {selectedSource.joins && selectedSource.joins.map(join => (
                    <div key={join.alias}>
                      <div className="text-sm font-medium text-gray-700 mb-2">
                        {join.label}
                      </div>
                      <div className="space-y-1">
                        {join.fields.map(field => (
                          <label key={`${join.alias}.${field.name}`} className="flex items-center space-x-2 text-sm hover:bg-gray-50 p-1 rounded cursor-pointer">
                            <input
                              type="checkbox"
                              checked={report.query_config.fields.includes(`${join.alias}.${field.name}`)}
                              onChange={() => toggleField(field.name, join.alias)}
                              className="rounded"
                            />
                            <span>{field.label}</span>
                            <span className="text-xs text-gray-400">({field.type})</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Middle Panel - Sort Order */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-gray-900">Sort Order</h3>
                <button
                  onClick={addSortField}
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  + Add Sort
                </button>
              </div>

              {report.query_config.orderBy.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">No sorting applied</p>
              ) : (
                <div className="space-y-3">
                  {report.query_config.orderBy.map((sort, index) => (
                    <div key={index} className="flex space-x-2">
                      <select
                        value={sort.field}
                        onChange={(e) => updateSortField(index, e.target.value, sort.direction)}
                        className="flex-1 px-3 py-2 border rounded-lg text-sm"
                      >
                        <option value="">Select field...</option>
                        {report.query_config.fields.map(field => (
                          <option key={field} value={field}>{field}</option>
                        ))}
                      </select>
                      <select
                        value={sort.direction}
                        onChange={(e) => updateSortField(index, sort.field, e.target.value)}
                        className="w-24 px-3 py-2 border rounded-lg text-sm"
                      >
                        <option value="ASC">A → Z</option>
                        <option value="DESC">Z → A</option>
                      </select>
                      <button
                        onClick={() => removeSortField(index)}
                        className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-gray-900">Filters</h3>
                <button
                  onClick={addFilter}
                  className="text-sm text-blue-600 hover:text-blue-700"
                  disabled={report.query_config.fields.length === 0}
                  title={report.query_config.fields.length === 0 ? "Select fields first" : ""}
                >
                  + Add Filter
                </button>
              </div>

              {report.query_config.filters.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">No filters applied</p>
              ) : (
                <div className="space-y-3">
                  {report.query_config.filters.map((filter, index) => {
                    const fieldType = filter.field ? getFieldType(filter.field) : 'text';
                    const operators = getOperatorsForType(fieldType);
                    const needsValue = operatorNeedsValue(filter.operator);

                    return (
                      <div key={index} className="border rounded-lg p-3 space-y-2 bg-gray-50">
                        {/* Field selector */}
                        <div className="flex space-x-2">
                          <select
                            value={filter.field}
                            onChange={(e) => {
                              const newType = getFieldType(e.target.value);
                              const defaultOp = getOperatorsForType(newType)[0].value;
                              updateFilter(index, e.target.value, defaultOp, filter.value);
                            }}
                            className="flex-1 px-3 py-2 border rounded-lg text-sm"
                          >
                            <option value="">Select field...</option>
                            {report.query_config.fields.map(field => (
                              <option key={field} value={field}>{field}</option>
                            ))}
                          </select>
                          <button
                            onClick={() => removeFilter(index)}
                            className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                          >
                            ✕
                          </button>
                        </div>

                        {/* Operator selector */}
                        {filter.field && (
                          <div className="flex space-x-2">
                            <select
                              value={filter.operator}
                              onChange={(e) => updateFilter(index, filter.field, e.target.value, filter.value)}
                              className="flex-1 px-3 py-2 border rounded-lg text-sm"
                            >
                              {operators.map(op => (
                                <option key={op.value} value={op.value}>{op.label}</option>
                              ))}
                            </select>
                          </div>
                        )}

                        {/* Value input */}
                        {filter.field && needsValue && (
                          <div>
                            {filter.operator === 'between' ? (
                              <div className="flex space-x-2">
                                <input
                                  type={fieldType === 'date' || fieldType === 'timestamp' ? 'date' : fieldType === 'number' || fieldType === 'integer' || fieldType === 'decimal' ? 'number' : 'text'}
                                  value={Array.isArray(filter.value) ? filter.value[0] : ''}
                                  onChange={(e) => {
                                    const newValue = [e.target.value, Array.isArray(filter.value) ? filter.value[1] : ''];
                                    updateFilter(index, filter.field, filter.operator, newValue);
                                  }}
                                  className="flex-1 px-3 py-2 border rounded-lg text-sm"
                                  placeholder="From"
                                />
                                <span className="flex items-center text-gray-500">to</span>
                                <input
                                  type={fieldType === 'date' || fieldType === 'timestamp' ? 'date' : fieldType === 'number' || fieldType === 'integer' || fieldType === 'decimal' ? 'number' : 'text'}
                                  value={Array.isArray(filter.value) ? filter.value[1] : ''}
                                  onChange={(e) => {
                                    const newValue = [Array.isArray(filter.value) ? filter.value[0] : '', e.target.value];
                                    updateFilter(index, filter.field, filter.operator, newValue);
                                  }}
                                  className="flex-1 px-3 py-2 border rounded-lg text-sm"
                                  placeholder="To"
                                />
                              </div>
                            ) : filter.operator === 'last_n_days' ? (
                              <input
                                type="number"
                                value={filter.value}
                                onChange={(e) => updateFilter(index, filter.field, filter.operator, parseInt(e.target.value) || 7)}
                                className="w-full px-3 py-2 border rounded-lg text-sm"
                                placeholder="Number of days"
                                min="1"
                              />
                            ) : (
                              <input
                                type={fieldType === 'date' || fieldType === 'timestamp' ? 'date' : fieldType === 'number' || fieldType === 'integer' || fieldType === 'decimal' ? 'number' : 'text'}
                                value={filter.value}
                                onChange={(e) => updateFilter(index, filter.field, filter.operator, e.target.value)}
                                className="w-full px-3 py-2 border rounded-lg text-sm"
                                placeholder="Value"
                              />
                            )}
                          </div>
                        )}

                        {/* Filter preview */}
                        {filter.field && (
                          <div className="text-xs text-gray-600 italic">
                            {filter.field} {operators.find(o => o.value === filter.operator)?.label.toLowerCase()} {needsValue ? (Array.isArray(filter.value) ? `${filter.value[0]} and ${filter.value[1]}` : filter.value || '...'): ''}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Grouping */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-gray-900">Group By</h3>
                <button
                  onClick={addGroupByField}
                  className="text-sm text-blue-600 hover:text-blue-700"
                  disabled={report.query_config.fields.length === 0}
                >
                  + Add Group
                </button>
              </div>

              {report.query_config.groupBy.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">No grouping</p>
              ) : (
                <div className="space-y-2">
                  {report.query_config.groupBy.map((field, index) => (
                    <div key={index} className="flex space-x-2">
                      <select
                        value={field}
                        onChange={(e) => updateGroupByField(index, e.target.value)}
                        className="flex-1 px-3 py-2 border rounded-lg text-sm"
                      >
                        <option value="">Select field...</option>
                        {report.query_config.fields.map(f => (
                          <option key={f} value={f}>{f}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => removeGroupByField(index)}
                        className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-gray-500 mt-2">
                Grouping organizes results by unique values in selected fields
              </p>
            </div>

            {/* Aggregates */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-gray-900">Aggregates</h3>
                <button
                  onClick={addAggregate}
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  + Add Aggregate
                </button>
              </div>

              {report.query_config.aggregates.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">No aggregates</p>
              ) : (
                <div className="space-y-3">
                  {report.query_config.aggregates.map((agg, index) => (
                    <div key={index} className="border rounded-lg p-3 space-y-2 bg-gray-50">
                      <div className="flex space-x-2">
                        <select
                          value={agg.func}
                          onChange={(e) => updateAggregate(index, e.target.value, agg.field, agg.alias)}
                          className="w-32 px-3 py-2 border rounded-lg text-sm"
                        >
                          <option value="COUNT">COUNT</option>
                          <option value="SUM">SUM</option>
                          <option value="AVG">AVG</option>
                          <option value="MIN">MIN</option>
                          <option value="MAX">MAX</option>
                        </select>
                        <select
                          value={agg.field}
                          onChange={(e) => updateAggregate(index, agg.func, e.target.value, agg.alias)}
                          className="flex-1 px-3 py-2 border rounded-lg text-sm"
                        >
                          <option value="*">* (count all)</option>
                          {getNumericFields().map(f => {
                            const fieldName = f.source === 'main' ? f.name : `${f.source}.${f.name}`;
                            return (
                              <option key={fieldName} value={fieldName}>
                                {f.label} ({f.source})
                              </option>
                            );
                          })}
                        </select>
                        <button
                          onClick={() => removeAggregate(index)}
                          className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                        >
                          ✕
                        </button>
                      </div>
                      <input
                        type="text"
                        value={agg.alias || ''}
                        onChange={(e) => updateAggregate(index, agg.func, agg.field, e.target.value)}
                        placeholder="Column alias (optional)"
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                      />
                      <div className="text-xs text-gray-600 italic">
                        {agg.func}({agg.field}) {agg.alias ? `as "${agg.alias}"` : ''}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-gray-500 mt-2">
                Aggregates calculate totals, averages, counts, etc. Best used with grouping.
              </p>
            </div>
          </div>

          {/* Right Panel - Preview */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Preview (First 10 Rows)</h3>

              {!previewData ? (
                <div className="text-center py-8 text-gray-500 text-sm">
                  Click "Preview" to see sample data
                </div>
              ) : (
                <div>
                  <div className="text-sm text-gray-600 mb-2">
                    {previewData.rowCount} rows • {previewData.executionTime}ms
                  </div>
                  <div className="overflow-x-auto max-h-96">
                    {previewData.data && previewData.data.length > 0 ? (
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            {Object.keys(previewData.data[0]).map(key => (
                              <th key={key} className="text-left px-2 py-1 font-semibold text-gray-700 border-b">
                                {key}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {previewData.data.map((row, i) => (
                            <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              {Object.values(row).map((val, j) => (
                                <td key={j} className="px-2 py-1 border-b text-gray-600">
                                  {val !== null && val !== undefined ? String(val) : '-'}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="text-center py-4 text-gray-500">No data returned</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReportBuilder;
